/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { formatCurrency } from '@ahm/common-helpers';
import { Button, Divider, Group, Image, Stack, Text, rem } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import InfoTooltip from '../info-tooltip';
import IconPromoTag from '@/assets/icons/promo-tag.svg';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type PaymentDetailsModalProps = {
  onClose: () => void;
};

const PROMOTION_LABEL = 'Khuyến mãi';

export default function PaymentDetailsModal({
  onClose,
}: PaymentDetailsModalProps) {
  const { estimatedData, paymentMethod: selectedPaymentMethod } =
    useBookingStore((state) => state);
  const tBooking = useTranslations('booking');

  // Helper function to get partner fee or regular fee
  const getFee = (key) =>
    estimatedData?.data?.[`partner_${key}`] ?? estimatedData?.data?.[key] ?? 0;

  const priceDetails = useMemo(() => {
    const userPriceDetails = estimatedData?.data?.user_price_details;
    if (!userPriceDetails) return userPriceDetails;

    return {
      ...userPriceDetails,
      details: userPriceDetails.details?.filter(
        (detail) => detail.name !== PROMOTION_LABEL
      ),
    };
  }, [estimatedData?.data?.user_price_details]);

  const discountDetails = useMemo(
    () => estimatedData?.data?.discount_details ?? [],
    [estimatedData?.data]
  );

  const savedAmount = useMemo(() => {
    return priceDetails?.total_discount_value;
  }, [priceDetails?.total_discount_value]);

  // Original price before discount
  const originalPrice = useMemo(() => {
    return priceDetails?.subtotal_price;
  }, [priceDetails?.subtotal_price]);

  return (
    <Stack pt={12} gap={0}>
      <Stack gap={rem(20)} py={rem(12)}>
        {priceDetails?.details.length > 0
          ? priceDetails.details.map((detail) => (
              <Stack key={detail.name} gap={0}>
                <Group justify="space-between">
                  <Stack gap={4}>
                    <Text fw={600} size="sm" c="neutral.7">
                      {detail.name}
                    </Text>
                    {detail.description && detail.hint ? (
                      <Text size="sm" c="neutral.6">
                        {detail.description} <InfoTooltip label={detail.hint} />
                      </Text>
                    ) : null}
                  </Stack>

                  {!detail.details ? (
                    <Text size="sm" fw={700} c="neutral.7">
                      {formatCurrency(detail.value)}
                    </Text>
                  ) : null}
                </Group>

                <Stack pl={16} gap={0}>
                  {detail.details
                    ? detail.details.map((subDetail) => (
                        <Group
                          key={subDetail.name}
                          justify="space-between"
                          py={8}
                        >
                          <Text size="sm" fw={500} c="neutral.6">
                            {subDetail.name}
                          </Text>
                          <Text size="sm" fw={700} c="neutral.7">
                            {formatCurrency(subDetail.value)}
                          </Text>
                        </Group>
                      ))
                    : null}
                </Stack>
              </Stack>
            ))
          : null}

        {/* Khuyến mãi */}
        {discountDetails.length > 0 && savedAmount ? (
          <>
            <Stack gap={8} pb={rem(16)}>
              {discountDetails.map((item, index) =>
                item.is_valid ? (
                  <Group key={index} justify="space-between" wrap="nowrap">
                    <Group gap={8} align="center" wrap="nowrap">
                      <IconPromoTag className="size-4 flex-none" />
                      <Text
                        fw={600}
                        fz={14}
                        lh={rem(20)}
                        c="neutral.7"
                        lineClamp={2}
                      >
                        {item.description || item.promo_code_id}
                      </Text>
                    </Group>
                    <Text fw={700} fz={14} lh={rem(20)} c="green.6">
                      -{formatCurrency(item.discount_value)}
                    </Text>
                  </Group>
                ) : null
              )}
            </Stack>
          </>
        ) : null}
      </Stack>

      <Divider />

      <Stack gap={rem(8)} py={rem(16)}>
        {savedAmount && savedAmount > 0 ? (
          <Group justify="space-between">
            <Text fw={500} size="sm" c="neutral.7">
              {tBooking('billing.saved_amount', {
                amount: formatCurrency(savedAmount),
              })}{' '}
              🎉
            </Text>

            {originalPrice ? (
              <Text
                fw={500}
                size="sm"
                c="neutral.6"
                style={{
                  textDecoration: 'line-through',
                }}
              >
                {formatCurrency(originalPrice)}
              </Text>
            ) : null}
          </Group>
        ) : null}

        {/* Tổng thanh toán */}
        <Group justify="space-between">
          <Stack gap={4}>
            <Text fw={700} size="md" c="neutral.7">
              {tBooking('billing.total_price')}
            </Text>
            <Text size="xs" fw={500} c="neutral.6">
              {tBooking('billing.vat')}
            </Text>
          </Stack>
          <Group mb={12} gap={8}>
            <Image src={selectedPaymentMethod?.icon_url} w={24} h={24} />

            <Text c="primary.6" size={rem(18)} fw={700}>
              {formatCurrency(getFee('total_price'))}
            </Text>
          </Group>
        </Group>
      </Stack>

      {/* COD */}
      {priceDetails?.total_cod ? (
        <>
          <Divider />
          <Group justify="space-between" py={16}>
            <Text size="sm" fw={600} c="neutral.7">
              COD
            </Text>
            <Text fw={700} size="sm" c="neutral.7">
              {formatCurrency(priceDetails.total_cod)}
            </Text>
          </Group>
        </>
      ) : null}
      <Button size="md" mih={rem(48)} onClick={onClose}>
        {tBooking('billing.got_it')}
      </Button>
    </Stack>
  );
}
